import { useState, useEffect } from "react";
import { Link } from "wouter";
import { X, Sparkles, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";

const SESSION_KEY = "drabpe_popup_shown";

export function AdPopup() {
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const timer = setTimeout(() => {
      setVisible(true);
      sessionStorage.setItem(SESSION_KEY, "1");
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  const slides = [
    {
      icon: TrendingUp,
      tag: "Advertise With DRABPE",
      headline: "Get Seen by Thousands of Durham Region Residents",
      body: "Banner ads, in-grid placements, and featured listings available. Plans from $10/week � support the community while growing your brand.",
      cta: "View Ad Packages",
      href: "/advertise",
      requiresAuth: false,
    },
    {
      icon: Users,
      tag: "Mentor Match 2026",
      headline: "Exclusive to Directory Members",
      body: "Get matched with a DRABPE-vetted mentor who knows your industry. Free for approved directory members. Applications open now.",
      cta: "Apply for Mentor Match",
      href: "/mentor-match",
      requiresAuth: true,
    },
    {
      icon: Sparkles,
      tag: "Member Benefits",
      headline: "Professional Services at Reduced Rates",
      body: "DRABPE directory members get exclusive pricing on legal, financial, web design, and marketing services from vetted partners.",
      cta: "Explore Services",
      href: "/services",
      requiresAuth: true,
    },
  ].filter((slide) => !slide.requiresAuth || !!user);

  if (!slides.length) return null;

  const current = slides[tab];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-0">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setVisible(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-3xl shadow-2xl overflow-hidden z-10"
        style={{ background: "linear-gradient(145deg, #102026 0%, #1a3a44 100%)" }}>
        {/* Glow */}
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #7eebb7 0%, transparent 60%)" }} />

        <button
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
          onClick={() => setVisible(false)}
          data-testid="button-close-popup"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative z-10 p-8">
          {/* Slide indicator dots */}
          <div className="flex items-center gap-1.5 mb-6">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setTab(i)}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === tab ? "24px" : "8px",
                  backgroundColor: i === tab ? "#7eebb7" : "rgba(126,235,183,0.3)",
                }}
              />
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: "rgba(126,235,183,0.15)" }}>
              <Icon className="w-6 h-6" style={{ color: "#7eebb7" }} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#7eebb7" }}>
                {current.tag}
              </div>
            </div>
          </div>

          <h3 className="text-xl font-bold text-white mb-3 leading-snug">
            {current.headline}
          </h3>
          <p className="text-sm leading-relaxed mb-8" style={{ color: "#d9c2a8" }}>
            {current.body}
          </p>

          <div className="flex flex-col gap-3">
            <Link href={current.href} onClick={() => setVisible(false)}>
              <Button className="w-full font-semibold h-11 rounded-xl text-sm"
                style={{ backgroundColor: "#7eebb7", color: "#102026" }}
                data-testid="button-popup-cta">
                {current.cta} ?
              </Button>
            </Link>
            <button
              className="w-full text-xs text-center py-2 transition-colors"
              style={{ color: "rgba(217,194,168,0.6)" }}
              onClick={() => setVisible(false)}
            >
              Not now, close this
            </button>
          </div>
        </div>

        {/* Nav buttons */}
        {tab < slides.length - 1 && (
          <div className="absolute bottom-4 right-4 z-10">
            <button
              className="text-xs px-3 py-1.5 rounded-lg font-medium"
              style={{ color: "#102026", backgroundColor: "rgba(126,235,183,0.4)" }}
              onClick={() => setTab(t => Math.min(t + 1, slides.length - 1))}
            >
              Next ?
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
